import {
  CloudUploadOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  PhoneOutlined,
  ShopOutlined,
  StarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { ShopFormModel } from "../../../interfaces/shop-form-model.interface";
import userService from "../../../services/user.service";
import "./shop-form.scss";

interface ShopFormProps {
  onSubmit: (values: any) => void;
  shop?: any;
}

const { Item } = Form;
const { Title, Text } = Typography;

const CLOUDINARY_CLOUD_NAME = "whappo";
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
const CLOUDINARY_UPLOAD_PRESET = "odm0pihn";
const CLOUDINARY_FOLDER = "shops";
const CLOUDINARY_DELIVERY_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const ShopForm: React.FC<ShopFormProps> = ({ onSubmit, shop }) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [usersFetching, setUsersFetching] = useState(false);
  const [creatorUser, setCreatorUser] = useState<any | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [publicImageId, setPublicImageId] = useState<string | undefined>(
    shop?.public_image_id,
  );
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(
    shop?.public_image_id
      ? `${CLOUDINARY_DELIVERY_URL}/${shop.public_image_id}`
      : undefined,
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (logoPreviewUrl && logoFile) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl, logoFile]);

  const handleSubmit = async (values: ShopFormModel) => {
    let formValue: any = null;

    if (values.location) {
      if (values.location.coordinates) {
        values.location.coordinates[0] = +values.location.coordinates[0];
        values.location.coordinates[1] = +values.location.coordinates[1];
      }

      values.location.lat = +values.location.lat;
      values.location.lng = +values.location.lng;
      values.location.lon = +values.location.lon;

      if (values.location.address) {
        values.formatted_address = values.location.address;
      }
    }

    try {
      let finalPublicImageId = publicImageId;

      if (logoFile) {
        setLogoUploading(true);

        const formData = new FormData();
        formData.append("file", logoFile);
        formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
        formData.append("folder", CLOUDINARY_FOLDER);

        const response = await axios.post(CLOUDINARY_UPLOAD_URL, formData);
        finalPublicImageId = response.data.public_id;

        setPublicImageId(finalPublicImageId);
        setLogoPreviewUrl(`${CLOUDINARY_DELIVERY_URL}/${finalPublicImageId}`);
      }

      if (!shop) {
        if (!creatorUser) {
          message.error("Creator user is required");
          return;
        }

        (values as any).creatorByUserEmail = creatorUser.email;
        (values as any).creatorByUserName = creatorUser.name;
        (values as any).scope = "whappo-deactive";
        (values as any).is_active = true;

        formValue = {
          ...values,
          public_image_id: finalPublicImageId,
        };
      } else {
        formValue = {
          ...shop,
          ...values,
          public_image_id: finalPublicImageId ?? shop.public_image_id,
        };
      }

      onSubmit(formValue);
    } catch (error) {
      console.error("Cloudinary upload or submit error", error);
      message.error("Shop could not be saved");
    } finally {
      setLogoUploading(false);
    }
  };

  const fetchUser = (value: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setSearchTerm(value);

    if (value.length < 3) {
      setUsers([]);
      setUsersFetching(false);
      return;
    }

    setUsersFetching(true);

    timeoutRef.current = setTimeout(() => {
      userService
        .getUsersByName(value)
        .then((response) => {
          setUsers(response || []);
        })
        .catch(() => {
          setUsers([]);
        })
        .finally(() => {
          setUsersFetching(false);
        });
    }, 500);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];

    if (!file) {
      setLogoFile(null);
      return;
    }

    setLogoFile(file);
    const preview = URL.createObjectURL(file);
    setLogoPreviewUrl(preview);
  };

  return (
    <div className="shop-form-page">
      <Card
        bordered={false}
        className="shop-form-hero"
        bodyStyle={{ padding: 24 }}
      >
        <div className="shop-form-hero__content">
          <div className="shop-form-hero__left">
            <div className="shop-form-hero__icon">
              <ShopOutlined />
            </div>

            <div>
              <Text className="shop-form-hero__eyebrow">
                {shop ? "Edit store" : "Create new store"}
              </Text>
              <Title level={2} className="shop-form-hero__title">
                {shop ? shop?.name || "Shop details" : "New shop"}
              </Title>
              <Text className="shop-form-hero__desc">
                Manage store identity, contact info, creator, location and
                operational details in one clean form.
              </Text>
            </div>
          </div>

          <div className="shop-form-logo-card">
            <div className="shop-form-logo-card__preview">
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Shop logo" />
              ) : (
                <div className="shop-form-logo-card__fallback">
                  {(form.getFieldValue("name") || shop?.name || "S")
                    .slice(0, 1)
                    .toUpperCase()}
                </div>
              )}
            </div>

            <div className="shop-form-logo-card__meta">
              <Text strong>Shop logo</Text>
              <Text type="secondary">
                Upload a clean square logo for a better storefront identity.
              </Text>

              <label className="shop-logo-upload-button">
                <CloudUploadOutlined />
                <span>{logoUploading ? "Uploading..." : "Choose image"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
              </label>

              {logoUploading && <Spin size="small" />}
            </div>
          </div>
        </div>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => handleSubmit(values as ShopFormModel)}
        initialValues={
          shop
            ? shop
            : {
                location: { type: "Point" },
              }
        }
        className="shop-form"
      >
        <Row gutter={[20, 20]}>
          <Col xs={24} xl={14}>
            <Card bordered={false} className="shop-form-card">
              <div className="shop-form-section-title">
                <ShopOutlined />
                <div>
                  <Title level={4}>Basic information</Title>
                  <Text type="secondary">
                    Main identity fields for the store.
                  </Text>
                </div>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24}>
                  <Item
                    label="Shop name"
                    name="name"
                    rules={[
                      { required: true, message: "Shop name is required" },
                    ]}
                  >
                    <Input size="large" placeholder="Enter shop name" />
                  </Item>
                </Col>

                <Col xs={24}>
                  <Item label="Description" name="description">
                    <Input.TextArea
                      rows={4}
                      placeholder="Write a short description for this shop"
                    />
                  </Item>
                </Col>

                {!shop || !shop._id ? (
                  <Col xs={24}>
                    <Item
                      label="Creator user"
                      name="creatorByUserId"
                      rules={[
                        { required: true, message: "Creator user is required" },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder="Search user by name"
                        notFoundContent={
                          searchTerm.length < 3 ? (
                            <span>Min. 3 characters for search</span>
                          ) : usersFetching ? (
                            <Spin size="small" />
                          ) : (
                            <span>Not found</span>
                          )
                        }
                        filterOption={false}
                        onSearch={fetchUser}
                        onChange={(val) => {
                          const found = users.find((x) => x._id === val);
                          setCreatorUser(found || null);
                        }}
                        showSearch
                        suffixIcon={<UserOutlined />}
                        style={{ width: "100%" }}
                      >
                        {users.map((user) => (
                          <Select.Option key={user._id} value={user._id}>
                            {user.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Item>
                  </Col>
                ) : null}

                <Col xs={24} md={12}>
                  <Item
                    label="Phone"
                    name="phone"
                    rules={[
                      { required: true, message: "Shop phone is required" },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter phone number"
                      prefix={<PhoneOutlined />}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Rating"
                    name="rating"
                    rules={[
                      { required: true, message: "Shop rating is required" },
                    ]}
                  >
                    <Input
                      size="large"
                      type="number"
                      placeholder="4.5"
                      prefix={<StarOutlined />}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Scope"
                    name="scope"
                    rules={[{ required: true, message: "Scope is required" }]}
                  >
                    <Input size="large" placeholder="Scope (whappo for app)" />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item label="Place ID" name="place_id">
                    <Input size="large" placeholder="Place ID" />
                  </Item>
                </Col>

                <Col xs={24}>
                  <Item label="Website" name="website">
                    <Input
                      size="large"
                      placeholder="https://example.com"
                      prefix={<GlobalOutlined />}
                    />
                  </Item>
                </Col>
              </Row>
            </Card>

            <Card bordered={false} className="shop-form-card">
              <div className="shop-form-section-title">
                <EnvironmentOutlined />
                <div>
                  <Title level={4}>Location details</Title>
                  <Text type="secondary">
                    Address and coordinate information for this store.
                  </Text>
                </div>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                  <Item label="Type" name={["location", "type"]}>
                    <Input size="large" placeholder="Point" />
                  </Item>
                </Col>

                <Col xs={24} md={16}>
                  <Item
                    label="Address"
                    name={["location", "address"]}
                    rules={[
                      { required: true, message: "Shop address is required" },
                    ]}
                  >
                    <Input size="large" placeholder="Enter full address" />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    label="Latitude (coordinates[0])"
                    name={["location", "coordinates", 0]}
                    rules={[
                      { required: true, message: "Latitude is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="Latitude" />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    label="Longitude (coordinates[1])"
                    name={["location", "coordinates", 1]}
                    rules={[
                      { required: true, message: "Longitude is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="Longitude" />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    label="Lng"
                    name={["location", "lng"]}
                    rules={[
                      { required: true, message: "Shop lng is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="Lng" />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Lat"
                    name={["location", "lat"]}
                    rules={[
                      { required: true, message: "Shop lat is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="Lat" />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Lon"
                    name={["location", "lon"]}
                    rules={[
                      { required: true, message: "Shop lon is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="Lon" />
                  </Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card bordered={false} className="shop-form-card">
              <div className="shop-form-section-title">
                <GlobalOutlined />
                <div>
                  <Title level={4}>Messaging and hours</Title>
                  <Text type="secondary">
                    Configure customer-facing messages and working hours.
                  </Text>
                </div>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24}>
                  <Item label="Completed message" name="completed_message">
                    <Input.TextArea
                      rows={4}
                      placeholder="Message shown after order completion"
                    />
                  </Item>
                </Col>

                <Col xs={24}>
                  <Item
                    label="Introduction message"
                    name="introduction_message"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Initial message shown to the customer"
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Opening hour"
                    name="opening_hour"
                    rules={[
                      { required: true, message: "Opening hour is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="0 - 23" />
                  </Item>
                </Col>

                <Col xs={24} md={12}>
                  <Item
                    label="Closing hour"
                    name="closing_hour"
                    rules={[
                      { required: true, message: "Closing hour is required" },
                    ]}
                  >
                    <Input size="large" type="number" placeholder="0 - 23" />
                  </Item>
                </Col>
              </Row>
            </Card>

            <Card
              bordered={false}
              className="shop-form-card shop-form-submit-card"
            >
              <Title level={4}>Ready to save?</Title>
              <Text type="secondary" className="shop-form-submit-card__text">
                Double-check the logo, contact information and location fields
                before submitting.
              </Text>

              <Button
                htmlType="submit"
                type="primary"
                size="large"
                disabled={logoUploading}
                className="shop-form-submit-button"
                block
              >
                {shop ? "Update shop" : "Create shop"}
              </Button>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ShopForm;
